import React, { useRef, useState, useMemo } from "react";
import SafeIcon from "../common/SafeIcon";
import { FaCamera, FaTimes, FaSyncAlt, FaExclamationTriangle } from "react-icons/fa";

const MAX_BYTES = 10 * 1024 * 1024;

const FileUpload = ({
  files = [],
  onFilesChange,
  error,
  title = "Upload your pay screenshot",
  tip,
}) => {
  const fileInputRef = useRef(null);
  const [localError, setLocalError] = useState("");

  const file = files?.[0] || null;

  // Create preview URL when file changes
  const previewUrl = useMemo(() => {
    if (!file) return "";
    return URL.createObjectURL(file);
  }, [file]);

  // Cleanup objectURL
  React.useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const openPicker = () => fileInputRef.current?.click();

  const setSingleFile = (f) => {
    onFilesChange(f ? [f] : []);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setLocalError("");

    if (selected.length > 1) {
      setLocalError("Only 1 screenshot is allowed. Please select a single image.");
      e.target.value = "";
      return;
    }

    const f = selected[0];
    if (!f) return;

    if (!f.type?.startsWith("image/")) {
      setLocalError("Please upload an image file (PNG, JPG, etc.).");
      e.target.value = "";
      return;
    }

    if (f.size > MAX_BYTES) {
      setLocalError("File is too large. Max size is 10MB.");
      e.target.value = "";
      return;
    }

    setSingleFile(f);
    e.target.value = "";
  };

  const removeFile = () => {
    setSingleFile(null);
    setLocalError("");
  };

  const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const displayError = localError || error;

  // --- UI ---
  if (file) {
    return (
      <div className="space-y-3">
        {/* Preview Card */}
        <div
          className={`overflow-hidden rounded-2xl border ${
            displayError ? "border-red-500" : "border-gray-200"
          } bg-white shadow-sm`}
        >
          {/* Image Preview */}
          <div className="relative">
            <img
              src={previewUrl}
              alt="Screenshot preview"
              className="w-full h-56 object-contain bg-gray-50"
            />

            {/* Top-right remove */}
            <button
              type="button"
              onClick={removeFile}
              className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm"
              aria-label="Remove screenshot"
              title="Remove"
            >
              <SafeIcon icon={FaTimes} className="w-4 h-4 text-gray-700" />
            </button>
          </div>

          {/* Meta + Actions */}
          <div className="p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatFileSize(file.size)} • Image
              </p>
            </div>

            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={openPicker}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                <SafeIcon icon={FaSyncAlt} className="w-4 h-4" />
                Replace
              </button>
            </div>
          </div>
        </div>

        {/* Helper row */}
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <SafeIcon icon={FaCamera} className="w-4 h-4" />
          Max 1 image • Up to 10MB
        </div>

        {/* Error */}
        {displayError && (
          <div className="rounded-2xl border border-red-500 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700 flex items-start gap-2">
              <SafeIcon icon={FaExclamationTriangle} className="w-4 h-4 mt-0.5" />
              {displayError}
            </p>
          </div>
        )}

        {/* Tip */}
        {tip && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs text-blue-700 font-semibold">💡 Tip: {tip}</p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={false}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  // Empty state (no file)
  return (
    <div className="space-y-3">
      <div
        className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-all ${
          displayError
            ? "border-red-500 bg-red-50"
            : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        }`}
        onClick={openPicker}
        role="button"
        tabIndex={0}
      >
        <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
          <SafeIcon icon={FaCamera} className="w-6 h-6 text-gray-600" />
        </div>

        <p className="text-base font-bold text-gray-900">{title}</p>
        <p className="text-sm text-gray-500 mt-1">Tap to choose 1 image (max 10MB)</p>
      </div>

      {displayError && (
        <div className="rounded-2xl border border-red-500 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-700 flex items-start gap-2">
            <SafeIcon icon={FaExclamationTriangle} className="w-4 h-4 mt-0.5" />
            {displayError}
          </p>
        </div>
      )}

      {tip && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs text-blue-700 font-semibold">💡 Tip: {tip}</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={false}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default FileUpload;
